import { ApiError, apiGet, apiPost, apiZod } from '@/shared/api'
import {
  type EmployeeJoin,
  EmployeeJoinSchema,
  type GuestLobbyEntry,
  GuestLobbyEntrySchema,
  type GuestLobbyState,
  GuestLobbyStateSchema,
  type GuestMeetingPreview,
  GuestMeetingPreviewSchema,
  type GuestSessionResult,
  GuestSessionSchema,
  type RoomAccess,
  RoomAccessSchema,
} from '../model/schema'

function toRoomAccess(dto: {
  meeting_id: string
  room_token: string
  signaling: string
}): RoomAccess {
  return RoomAccessSchema.parse({
    meetingId: dto.meeting_id,
    roomToken: dto.room_token,
    signaling: dto.signaling,
  })
}

// Сотрудник входит в свою встречу. Вызов идемпотентный: в закрытом звонке он же
// служит поллингом допуска — state=waiting без room-JWT, joined с ним.
export async function joinMeeting(meetingId: string): Promise<EmployeeJoin> {
  const body = await apiPost(`/meetings/${meetingId}/join`, apiZod.zJoinMeetingResponse)
  return EmployeeJoinSchema.parse({
    participantId: body.data.participant_id,
    state: body.data.state,
    meetingState: body.data.meeting_state,
    roomAccess: body.data.room_access ? toRoomAccess(body.data.room_access) : undefined,
  })
}

// Превью встречи по guest-токену — валидация ссылки до формы имени
// (404 = битая ссылка; ended/cancelled — не ошибка, а честный экран).
export async function fetchGuestMeeting(token: string): Promise<GuestMeetingPreview> {
  const body = await apiGet(`/guest/${token}/meeting`, apiZod.zGuestMeetingPreviewResponse)
  return GuestMeetingPreviewSchema.parse({
    title: body.data.title,
    state: body.data.meeting_state,
    scheduledAt: body.data.scheduled_at ?? undefined,
  })
}

// Гость входит по ссылке → попадает в lobby (ожидает допуска).
export async function joinAsGuest(token: string, name: string): Promise<GuestLobbyEntry> {
  const body = await apiPost(`/guest/${token}/join`, apiZod.zJoinMeetingAsGuestResponse, { name })
  return GuestLobbyEntrySchema.parse({
    meetingId: body.data.meeting_id,
    participantId: body.data.participant_id,
    state: body.data.state,
  })
}

// Восстановление гостевого контекста из HttpOnly-cookie (ставится join'ом).
// HTTP-отказы — осмысленный результат, не исключение: 404 = куки нет (none),
// MEETING_ENDED/`MEETING_CANCELLED` = гость был, но встреча закончилась.
// Сетевые сбои пробрасываются — пусть query ретраит, а не выкидывает гостя.
export async function fetchGuestSession(): Promise<GuestSessionResult> {
  try {
    const body = await apiGet('/guest/session', apiZod.zGuestSessionResponse)
    const session = GuestSessionSchema.parse({
      token: body.data.token,
      participantId: body.data.participant_id,
      meetingId: body.data.meeting_id,
      guestName: body.data.guest_name,
      state: body.data.state,
      meetingState: body.data.meeting_state,
      roomAccess: body.data.room_access ? toRoomAccess(body.data.room_access) : undefined,
    })
    return { kind: 'active', session }
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.code?.toUpperCase() === 'MEETING_ENDED') return { kind: 'ended' }
      if (error.code?.toUpperCase() === 'MEETING_CANCELLED') return { kind: 'cancelled' }
      return { kind: 'none' }
    }
    throw error
  }
}

// Гость опрашивает свой lobby-статус; после допуска приходит room-JWT.
export async function fetchGuestState(
  token: string,
  participantId: string,
): Promise<GuestLobbyState> {
  const body = await apiGet(`/guest/${token}/state`, apiZod.zGuestLobbyStateResponse, {
    participant_id: participantId,
  })
  return GuestLobbyStateSchema.parse({
    state: body.data.state,
    meetingState: body.data.meeting_state,
    roomAccess: body.data.room_access ? toRoomAccess(body.data.room_access) : undefined,
  })
}
